import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CircleIcon from "@mui/icons-material/Circle";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Modal,
  Pagination,
  Paper,
  TextField,
  Typography,
  Tab,
  Tabs,
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import { styled } from "@mui/material/styles";
import axios from "axios";
import { format } from "date-fns";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AdminMessageGroupCarousel from "./AdminMessageGroupCarousel";
import TextActivityDashboard from "./TextActivityDashboard";

// Styled components from ChatScreen
const ImagePreviewModal = styled(Modal)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const CloseButton = styled(IconButton)({
  position: "absolute",
  right: 16,
  top: 16,
  color: "white",
  backgroundColor: "rgba(0, 0, 0, 0.4)",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.6)",
  },
});

const ParsedDataContainer = styled(Box)({
  backgroundColor: "rgba(28, 28, 30, 0.95)",
  padding: "20px",
  borderRadius: "8px",
  maxHeight: "80vh",
  overflowY: "auto",
  color: "white",
  width: "400px",
});

const AdminPage = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "conversations"; // Default to conversations
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [imageUrls, setImageUrls] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const messagesContainerRef = useRef(null); // Ref for messages container

  const fetchImageUrls = useCallback(async (messages) => {
    if (!Array.isArray(messages) || messages.length === 0) {
      return;
    }

    const allImageKeys = messages
      .filter((msg) => msg.images?.length > 0)
      .flatMap((msg) => msg.images);

    if (allImageKeys.length === 0) {
      return;
    }

    try {
      const { data } = await axios.post("/api/admin/get_activity_image_urls", {
        s3_keys: allImageKeys,
      });

      const urlMapping = {};
      allImageKeys.forEach((key, index) => {
        if (data.urls && data.urls[index]) {
          urlMapping[key] = data.urls[index];
        }
      });
      setImageUrls((prevUrls) => ({ ...prevUrls, ...urlMapping }));
    } catch (error) {
      console.error("Error fetching image URLs:", error);
    }
  }, []);

  // Updated handleUserSelect to only update URL
  const handleUserSelect = (userId) => {
    const currentUserIdInParams = searchParams.get("user_id");
    const newUserIdString = userId.toString();

    if (currentUserIdInParams !== newUserIdString) {
      const params = new URLSearchParams(searchParams);
      // Ensure other relevant params like 'tab' are preserved or explicitly set
      // searchParams already contains the current tab, so it will be preserved
      // if not overwritten.
      params.set("user_id", newUserIdString);
      navigate({ search: params.toString() });
    }
  };

  const handleImageClick = (imageUrl, message) => {
    setPreviewImage(imageUrl);
    setSelectedMessage(message);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewImage(null);
    setPreviewOpen(false);
  };

  // Function to format the transcript
  const renderTranscript = (transcript) => {
    return transcript.map((entry, index) => (
      <Box key={index} sx={{ mb: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{
            color: entry.name.toLowerCase() === "me" ? "#FF3B30" : "#4CAF50",
            fontWeight: "bold",
          }}
        >
          {entry.name}:
        </Typography>
        {entry.messages.map((msg, msgIndex) => (
          <Typography
            key={msgIndex}
            variant="body2"
            sx={{
              color: "rgba(255, 255, 255, 0.87)",
              ml: 2,
            }}
          >
            {msg}
          </Typography>
        ))}
      </Box>
    ));
  };

  // Update the renderMessage function
  const renderMessage = (message) => {
    if (message.message_type === "message_groups") {
      try {
        const messageGroups =
          typeof message.content === "string"
            ? JSON.parse(message.content)
            : message.content;

        return (
          <Box sx={{ position: "relative" }}>
            {message.copied_messages && message.copied_messages.length > 0 && (
              <CheckCircleIcon
                sx={{
                  position: "absolute",
                  top: -10,
                  left: -10,
                  color: "#4CAF50",
                  backgroundColor: "black",
                  borderRadius: "50%",
                  zIndex: 1,
                }}
              />
            )}
            <AdminMessageGroupCarousel
              messageId={message.id}
              messageGroups={messageGroups}
              metadata={message.metadata}
              timestamp={message.timestamp}
              copiedMessageIndices={message.copied_messages?.map((msg) => ({
                outer: msg.outer_index,
                inner: msg.inner_index,
              }))}
            />
          </Box>
        );
      } catch (error) {
        console.error("Error parsing message groups:", error, message.content);
        return null;
      }
    }

    // For messages with only images and no content
    if (!message.content && message.images?.length > 0) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent:
              message.sender_type === "user" ? "flex-end" : "flex-start",
            mb: 2,
          }}
        >
          <Box sx={{ maxWidth: "70%" }}>
            {message.images.map((image, index) => (
              <Box
                key={index}
                component="img"
                src={imageUrls[image]}
                onClick={() => handleImageClick(imageUrls[image], message)}
                sx={{
                  width: "100%",
                  maxHeight: "300px",
                  objectFit: "contain",
                  borderRadius: 2,
                  mb: 1,
                  bgcolor: "#1C1C1E",
                  cursor: "pointer",
                }}
                alt={`User uploaded image ${index + 1}`}
              />
            ))}
          </Box>
        </Box>
      );
    }

    // For messages with both content and images
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent:
            message.sender_type === "user" ? "flex-end" : "flex-start",
          mb: 2,
        }}
      >
        <Paper
          sx={{
            p: 2,
            maxWidth: "70%",
            bgcolor: message.sender_type === "user" ? "#FF3B30" : "#1C1C1E",
            color: "white",
            borderRadius: 2,
          }}
        >
          {message.content && <Typography>{message.content}</Typography>}
          {message.images?.length > 0 && (
            <Box sx={{ mt: message.content ? 1 : 0 }}>
              {message.images.map((image, index) => (
                <Box
                  key={index}
                  component="img"
                  src={imageUrls[image]}
                  onClick={() => handleImageClick(imageUrls[image], message)}
                  sx={{
                    width: "100%",
                    maxHeight: "300px",
                    objectFit: "contain",
                    borderRadius: 1,
                    mb: index < message.images.length - 1 ? 1 : 0,
                    cursor: "pointer",
                  }}
                  alt={`User uploaded image ${index + 1}`}
                />
              ))}
            </Box>
          )}
        </Paper>
      </Box>
    );
  };

  const handleSearch = async () => {
    if (!searchInputValue.trim()) {
      setSearchQuery("");
      return;
    }

    setSearchQuery(searchInputValue);
    setIsSearching(true);
    const messageLimitFromParams = searchParams.get("message_limit");
    const messageLimit =
      parseInt(messageLimitFromParams, 10) > 0
        ? parseInt(messageLimitFromParams, 10)
        : 250;

    try {
      const { data } = await axios.get(
        `/api/admin/get_all_text_activities?search=${searchInputValue}&page=1&page_size=${pageSize}&message_limit=${messageLimit}`
      );
      setActivities(data.data || []);
      setTotalPages(Math.ceil(data.total / pageSize));
      setPage(1);
    } catch (error) {
      console.error("Error searching:", error);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  useEffect(() => {
    const checkAdminAndFetchData = async () => {
      try {
        const { data: adminData } = await axios.get("/api/user/check_admin");
        if (!adminData.is_superuser) {
          navigate("/");
          return;
        }

        const messageLimitFromParams = searchParams.get("message_limit");
        const messageLimit =
          parseInt(messageLimitFromParams, 10) > 0
            ? parseInt(messageLimitFromParams, 10)
            : 250;

        if (!searchQuery) {
          const { data } = await axios.get(
            `/api/admin/get_all_text_activities?page=${page}&page_size=${pageSize}&message_limit=${messageLimit}`
          );
          setActivities(data.data || []);
          setTotalPages(Math.ceil(data.total / pageSize));
        }
        setLoading(false);
      } catch (err) {
        console.log(err);
        if (err.response?.status === 403) {
          navigate("/");
        } else {
          setError(
            err.response?.data?.error || "An error occurred while fetching data"
          );
        }
        setLoading(false);
      }
    };

    checkAdminAndFetchData();
  }, [navigate, page, pageSize, searchQuery]); // searchQuery dependency handles re-fetching or relying on search results

  // Effect to handle user selection based on URL param and fetch data
  useEffect(() => {
    const userIdFromParams = searchParams.get("user_id");

    if (userIdFromParams) {
      if (!loading) {
        if (activities.length > 0) {
          const userToSelect = activities.find(
            (u) => u.user_id.toString() === userIdFromParams
          );

          if (userToSelect) {
            if (
              !selectedUser ||
              selectedUser.user_id.toString() !== userIdFromParams
            ) {
              setSelectedUser(userToSelect);
              if (userToSelect.conversation?.messages) {
                fetchImageUrls(userToSelect.conversation.messages);
              }
            }
          } else {
            // User ID in URL, not loading, but user not found in current activities.
            if (selectedUser) setSelectedUser(null);

            // Construct new search params, preserving everything EXCEPT user_id
            const newSearchEntries = new URLSearchParams();
            for (const [key, value] of searchParams.entries()) {
              if (key !== "user_id") {
                newSearchEntries.append(key, value);
              }
            }
            navigate(
              { search: newSearchEntries.toString() },
              { replace: true }
            );
          }
        } else {
          // Not loading, but activities array is empty.
          if (selectedUser) setSelectedUser(null);
          const newSearchEntries = new URLSearchParams();
          for (const [key, value] of searchParams.entries()) {
            if (key !== "user_id") {
              newSearchEntries.append(key, value);
            }
          }
          navigate({ search: newSearchEntries.toString() }, { replace: true });
        }
      }
    } else {
      if (selectedUser) {
        setSelectedUser(null);
      }
    }
  }, [
    searchParams,
    activities,
    loading,
    navigate,
    selectedUser,
    fetchImageUrls,
  ]);

  // Effect to auto-scroll messages to the bottom
  useEffect(() => {
    if (
      selectedUser &&
      selectedUser.conversation?.messages &&
      messagesContainerRef.current
    ) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [selectedUser?.conversation?.messages]); // Auto-scroll when messages of selected user change

  // Update hasRecentActivity function to use new data structure
  const hasRecentActivity = (userData) => {
    if (!userData.conversation?.last_message_at) return false;
    const latestActivity = new Date(
      userData.conversation.last_message_at
    ).getTime();
    const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;
    return latestActivity > twentyFourHoursAgo;
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleTabChange = (event, newValue) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", newValue);

    // Reset the ID parameter when switching between activities and tracked tabs
    if (
      (activeTab === "activities" && newValue === "tracked") ||
      (activeTab === "tracked" && newValue === "activities")
    ) {
      params.delete("id");
    }

    // Preserve filter_to_user_id if it exists
    if (searchParams.get("filter_to_user_id")) {
      params.set("filter_to_user_id", searchParams.get("filter_to_user_id"));
    }

    navigate({
      pathname: "/admin",
      search: params.toString(),
    });
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 100px)",
        bgcolor: "black",
        mt: 5,
      }}
    >
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          "& .MuiTab-root": {
            color: "rgba(255, 255, 255, 0.7)",
            "&.Mui-selected": {
              color: "#FF3B30",
            },
          },
        }}
      >
        <Tab label="Conversations" value="conversations" />
        <Tab label="Text Activities" value="activities" />
        <Tab label="Tracked Activities" value="tracked" />
      </Tabs>

      {activeTab === "conversations" ? (
        <Box
          sx={{
            display: "flex",
            height: "calc(100vh - 100px)",
            bgcolor: "black",
            mt: 5,
          }}
        >
          {/* Users List - Left Column */}
          <Box
            sx={{
              width: "20%",
              borderRight: "1px solid rgba(255, 255, 255, 0.12)",
              overflow: "auto",
              bgcolor: "#1C1C1E",
              display: "flex",
              flexDirection: "column",
              maxHeight: "calc(100vh - 100px)",
            }}
          >
            {/* Search Box and Pagination */}
            <Box
              sx={{ p: 2, borderBottom: "1px solid rgba(255, 255, 255, 0.12)" }}
            >
              <TextField
                fullWidth
                placeholder="Search users..."
                value={searchInputValue}
                onChange={(e) => setSearchInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton
                        onClick={handleSearch}
                        sx={{
                          p: "4px",
                          color: "rgba(255, 255, 255, 0.7)",
                          "&:hover": {
                            color: "#FF3B30",
                          },
                        }}
                      >
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    color: "white",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255, 255, 255, 0.12)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255, 255, 255, 0.2)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#FF3B30",
                    },
                    "& input": {
                      color: "white",
                      "&::placeholder": {
                        color: "rgba(255, 255, 255, 0.5)",
                        opacity: 1,
                      },
                    },
                  },
                }}
                size="small"
              />
              {!searchQuery && (
                <Box sx={{ mt: 1 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    size="small"
                    sx={{
                      "& .MuiPaginationItem-root": {
                        color: "white",
                        fontSize: "0.8rem",
                      },
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* Users List */}
            <List sx={{ flex: 1, overflow: "auto" }}>
              {isSearching ? (
                <Box sx={{ p: 2, textAlign: "center" }}>
                  <CircularProgress size={24} sx={{ color: "#FF3B30" }} />
                </Box>
              ) : activities.length === 0 ? (
                <Box sx={{ p: 2, textAlign: "center" }}>
                  <Typography sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
                    No users found
                  </Typography>
                </Box>
              ) : (
                activities.map((user) => (
                  <ListItem
                    key={user.user_id}
                    button
                    selected={selectedUser?.user_id === user.user_id}
                    onClick={() => handleUserSelect(user.user_id)}
                    sx={{
                      "&.Mui-selected": {
                        bgcolor: "rgba(255, 59, 48, 0.08)",
                        "&:hover": {
                          bgcolor: "rgba(255, 59, 48, 0.12)",
                        },
                      },
                      "&:hover": {
                        bgcolor: "rgba(255, 255, 255, 0.04)",
                      },
                    }}
                  >
                    <ListItemText
                      primary={user.first_name || "Anonymous"}
                      secondary={
                        <>
                          {user.phone_number || "No phone"}
                          <br />
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.7rem",
                              color: "rgba(255, 255, 255, 0.5)",
                            }}
                          >
                            ID: {user.user_id}
                          </Typography>
                          <br />
                          Last active:{" "}
                          {format(
                            new Date(user.conversation.last_message_at),
                            "MMM d, h:mm a"
                          )}
                        </>
                      }
                      sx={{
                        "& .MuiListItemText-primary": {
                          color: "white",
                        },
                        "& .MuiListItemText-secondary": {
                          color: "rgba(255, 255, 255, 0.7)",
                        },
                      }}
                    />
                    {hasRecentActivity(user) && (
                      <CircleIcon
                        sx={{
                          color: "#4caf50",
                          fontSize: 12,
                          ml: 1,
                        }}
                      />
                    )}
                  </ListItem>
                ))
              )}
            </List>
          </Box>

          {/* Conversation View - Right Column */}
          <Box
            sx={{
              width: "80%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              bgcolor: "black",
            }}
          >
            {selectedUser ? (
              <>
                {/* User Info Header */}
                <Box
                  sx={{
                    p: 2,
                    borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
                    bgcolor: "#1C1C1E",
                  }}
                >
                  <Typography variant="h6" sx={{ color: "white" }}>
                    {selectedUser.first_name || "Anonymous"}
                    <Typography
                      component="span"
                      sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                    >
                      ({selectedUser.phone_number || "No phone"})
                    </Typography>
                  </Typography>
                </Box>

                {/* Messages Container */}
                <Box
                  ref={messagesContainerRef}
                  sx={{
                    flex: 1,
                    overflow: "auto",
                    p: 2,
                  }}
                >
                  {selectedUser.conversation.messages
                    .slice()
                    .reverse()
                    .map((message) => (
                      <Box key={message.id}>{renderMessage(message)}</Box>
                    ))}
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Typography sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
                  Select a user to view their conversation
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      ) : activeTab === "activities" ? (
        <TextActivityDashboard />
      ) : (
        <TextActivityDashboard isTracked={true} />
      )}

      {/* Image Preview Modal */}
      <ImagePreviewModal
        open={previewOpen}
        onClose={handleClosePreview}
        closeAfterTransition
        slotProps={{
          backdrop: {
            timeout: 500,
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.9)",
            },
          },
        }}
      >
        <Fade in={previewOpen}>
          <Box
            sx={{
              outline: "none",
              position: "relative",
              display: "flex",
              gap: 3,
              maxWidth: "90vw",
              margin: "0 auto",
            }}
          >
            <CloseButton
              onClick={handleClosePreview}
              sx={{
                position: "absolute",
                right: "16px",
                top: "16px",
                zIndex: 1,
              }}
            >
              <CloseIcon />
            </CloseButton>

            {/* Image */}
            <Box
              component="img"
              src={previewImage}
              sx={{
                maxHeight: "80vh",
                maxWidth: "60vw",
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />

            {/* Parsed Data */}
            {selectedMessage?.parsed_data_from_images && (
              <ParsedDataContainer>
                {/* Background */}
                <Typography variant="h6" sx={{ mb: 2, color: "#FF3B30" }}>
                  Background
                </Typography>
                <Typography variant="body2" sx={{ mb: 3 }}>
                  {selectedMessage.parsed_data_from_images.background}
                </Typography>

                {/* Profile Details */}
                <Typography variant="h6" sx={{ mb: 2, color: "#FF3B30" }}>
                  Profile Details
                </Typography>
                <Typography variant="body2" sx={{ mb: 3 }}>
                  {
                    selectedMessage.parsed_data_from_images
                      .dating_profile_details
                  }
                </Typography>

                {/* Other Person */}
                <Typography variant="h6" sx={{ mb: 2, color: "#FF3B30" }}>
                  Other Person
                </Typography>
                <Typography variant="body2" sx={{ mb: 3 }}>
                  {selectedMessage.parsed_data_from_images.other_person_name}
                </Typography>

                {/* Transcript */}
                {selectedMessage.parsed_data_from_images.transcript?.length >
                  0 && (
                  <>
                    <Typography variant="h6" sx={{ mb: 2, color: "#FF3B30" }}>
                      Transcript
                    </Typography>
                    {renderTranscript(
                      selectedMessage.parsed_data_from_images.transcript
                    )}
                  </>
                )}

                {/* Observations */}
                {selectedMessage.parsed_data_from_images.observations?.length >
                  0 && (
                  <>
                    <Typography variant="h6" sx={{ mb: 2, color: "#FF3B30" }}>
                      Observations
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                      {selectedMessage.parsed_data_from_images.observations.map(
                        (obs, index) => (
                          <Typography
                            key={index}
                            variant="body2"
                            sx={{ mb: 1 }}
                          >
                            • {obs}
                          </Typography>
                        )
                      )}
                    </Box>
                  </>
                )}
              </ParsedDataContainer>
            )}
          </Box>
        </Fade>
      </ImagePreviewModal>
    </Box>
  );
};

export default AdminPage;
